import React, { memo, useEffect } from "react";
import "./styles.scss";
import ProductCard from "Components/Productcard";
import { useLocation, useNavigate } from "react-router-dom";
import { SkeletonLine } from "Components/Skeletion";
import ModelNew from "Components/Model/ModelNew";
import Button from "Components/Common/Button";
import { useWindowSize } from "Utilities";
import AdvancedLink from "Components/AdvancedLink";
import { useTranslation } from 'react-i18next';

const SuggestionSearch = ({
  loading = true,
  searchKeyword,
  data,
  setShowSuggestion,
  openSearch,
}) => {
  const navigate = useNavigate();
  const [width] = useWindowSize();

  const searchResultHandler = () => {
    setShowSuggestion(false);
    const trimmedKeyword = searchKeyword.trim();
    if (trimmedKeyword) {
      const encodedKeyword = encodeURIComponent(trimmedKeyword);
      navigate(`/zoeken/zoekterm=${encodedKeyword}`, {
        state: {
          isSearchResult: true,
          value: encodedKeyword,
        },
      });
    }
  };

  const productsToShow = width <= 768 ? data?.products?.slice(0, 4) : data?.products;
  const noResults =
    !loading &&
    !data?.products?.length &&
    !data?.category?.length &&
    data?.response?.status === false;

  return (
    <div className={`suggestion__search ${openSearch ? "" : "open__search"}`}>
      <div className="container pl-6 pr-5 xxl-pl-6 xxl-pr-0">
        <div className="suggestion__wrapper xl-pt-6 pt-sm-2 pb-8">
          {searchKeyword?.length < 3 ? (
            <MessageBlock text="Waar bent u naar op zoek?" />
          ) : noResults ? (
            <MessageBlock text={data?.response?.message} />
          ) : (
            <>
              <HeaderSection loading={loading} data={data} />
              <ProductSection loading={loading} products={productsToShow} />
              <SeeAllResults
                loading={loading}
                hasProducts={!!data?.products?.length}
                onClick={searchResultHandler}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const HeaderSection = ({ loading, data }) => (
  <div className="flex col left gap-x-15 xl-flex xl-row pt-2 xl-space-between xl-middle">
    <div className="title__block pb-5 xl-pb-0 pt-2">
      {loading ? (
        <SkeletonLine animation="pulse" width="200px" height="35px" style={{ borderRadius: "20px" }} />
      ) : (
        <h3 className="fw-700 line-9 text-nowrap">{data?.productCount} resultaten gevonden</h3>
      )}
    </div>
    <div className="seract__item overflow-hidden overflow-x-auto no-scrollbar" style={{ width:"100%" }}>
      <div className="btn__blocks flex middle gap-x-4 nowrap">
        {loading
          ? Array(4).fill(null).map((_, i) => (
              <SkeletonLine
                key={i}
                animation="pulse"
                width="100px"
                height="35px"
                style={{ borderRadius: "20px" }}
              />
            ))
          : data?.category?.map((cat, idx) => (
              <AdvancedLink className="button r-5" to={`/${cat?.urlKey}`} state={{currentPageView: cat?.plp_design_view }} key={idx}>
                {cat?.catName}
              </AdvancedLink>
            ))}
      </div>
    </div>
  </div>
);

const ProductSection = ({ loading, products }) => (
  <div className="product__reults pt-8 xl-pt-11">
    <div className="flex gap-x-7 pb-3 product__reults_MobRes">
      {loading
        ? Array(4).fill(null).map((_, i) => (
            <ProductCard loading={true} key={i} pageName="search" />
          ))
        : products?.map((item, i) => (
            <ProductCard data={item} key={i} plp={true} pageName="search" />
          ))}
    </div>
  </div>
);

const SeeAllResults = ({ loading, hasProducts, onClick }) => {
  const { t } = useTranslation();

  return (
    <div className="see__all__results pt-4 md-pt-14 pb-3 tc">
      {loading ? (
        <SkeletonLine animation="pulse" width="250px" height="50px" style={{ borderRadius: "20px" }} />
      ) : hasProducts ? (
        <Button className="r-5 r-6 fw-700" onClick={onClick}>
          {t("Showallresults")}
        </Button>
      ) : null}
    </div>
  );
};

const MessageBlock = ({ text }) => (
  <div className="msg__block flex center middle py-8">
    <h4 className="fw-500 px-6 py-4 r-2 overflow">{text}</h4>
  </div>
);

const SearchSuggestion = ({
  openModel,
  setOpenModel,
  loading,
  searchKeyword,
  searchData,
  setShowSuggestion,
  showSuggestion,
  hideScroll = false,
}) => {
  const location = useLocation();

  useEffect(() => {
    if (showSuggestion) setShowSuggestion(false);
  }, [location]);

  return (
    <div className="search__suggestion__container">
      <ModelNew
        from="top"
        hideScroll={hideScroll}
        zindex={9}
        open={openModel}
        shadow={true}
        setOpen={setOpenModel}
      >
        <SuggestionSearch
          loading={loading}
          searchKeyword={searchKeyword}
          data={searchData}
          setShowSuggestion={setShowSuggestion}
          openSearch={openModel}
        />
      </ModelNew>
    </div>
  );
};

export default memo(SearchSuggestion);